import { useEffect, useState, useMemo } from "react";
import FieldBage from "@components/global/new/FieldBage/FieldBage";
import { ALGOS_E } from "@constants/Algos";
import {
  calculateHash,
  getDevivedHash,
  HashArr,
  HashFactorName,
} from "@utils/hash.utils";
import MinerViewBlock from "../MinerViewBlock/MinerViewBlock";
import { useIntl } from "react-intl";
import { IMinerProfileFieldsProps } from "./MinerProfileFields.props";
import HashSelector from "@components/global/new/HashSelector/HashSelector";
import styles from "./MinerProfileFields.module.scss";
import { IMinerProfileMeta } from "@services/minerProfiles";
import ConsumptionField from "./ConsumptionField/ConsumptionField";
import Efficiency from "./Efficiency/Efficiency";
import { Input } from "@components/global/new/Input/Input";

function MinerProfileFields(props: IMinerProfileFieldsProps) {
  const additional: IMinerProfileMeta = useMemo(() => {
    if (typeof props.currentProfile?.meta === "string") {
      return JSON.parse(props.currentProfile?.meta || "{}");
    }
    return props.currentProfile?.meta || {};
  }, [props.currentProfile?.meta]);
  const { formatMessage } = useIntl();
  const [consumption, setConsumption] = useState<number>(0);
  const [perf, setPerf] = useState<number>(0);
  const [selectedAlgo, setSelectedAlgo] = useState<Omit<ALGOS_E, "ALL">>(
    props.currentProfile?.hash || ALGOS_E.SHA256
  );

  useEffect(() => {
    setPerf(props.currentProfile?.performance || 0);
    setSelectedAlgo(props.currentProfile?.hash || ALGOS_E.SHA256);
  }, [props.currentProfile]);

  //console.log(props.errors);

  const checkErrors = (name: string) => {
    if (!props.errors) return false;
    return !!props.errors[name];
  };

  const onChange = (
    numb: number,
    type: "perf" | "cons",
    bage?: HashFactorName
  ) => {
    if (type === "perf" && !bage) return;
    switch (type) {
      case "perf":
        setPerf(calculateHash(numb, bage as HashFactorName));
        break;
      case "cons":
        setConsumption(numb);
        break;
      default:
        return;
    }
  };

  const onChangeAlgo = (algo: typeof selectedAlgo) => {
    setSelectedAlgo(algo);
  };

  const [perfNum, perfBage] = useMemo(() => {
    return getDevivedHash(perf, { unit: selectedAlgo as ALGOS_E }).split(" ");
  }, [perf, selectedAlgo]);

  const effectiveBage = useMemo(() => {
    const kw = formatMessage({ id: "calc.cons.bage" });
    const [unit] = perfBage.split("/");

    return `${unit}/${kw}`;
  }, [selectedAlgo, perf, perfBage]);

  return (
    <div className={styles.wrapper}>
      <MinerViewBlock
        disabled={props.disabled}
        meta={additional}
        checkErrors={checkErrors}
        reset={props.reset}
      />
      <div className={styles.content}>
        <Input
          type="text"
          size="small"
          label={
            <span className={styles.label}>
              {formatMessage({ id: "contracts.info.name" })}
            </span>
          }
          outlined={!props.disabled}
          readOnly={props.disabled}
          defaultValue={props.currentProfile?.name}
          status={(checkErrors("name") && "error") || ""}
          id="name"
        />
        <FieldBage
          name={formatMessage({ id: "calc.perf" })}
          selector={HashArr}
          bageAlgo={selectedAlgo as ALGOS_E}
          readonly={props.disabled}
          outlined={!props.disabled}
          id="perfomance"
          isError={checkErrors("perfomance")}
          up
          defaultValue={Number(perfNum)}
          oldValue={Number(perfNum)}
          bage={perfBage}
          hasPrevValue={false}
          getValue={(v, bage) =>
            onChange(Number(v), "perf", bage as HashFactorName)
          }
          reset={props.reset}
        />
        <ConsumptionField
          disabled={props.disabled}
          outlined={!props.disabled}
          onChangeValue={(v) => setConsumption(v)}
          isError={checkErrors("consumpation")}
          cons={props.currentProfile?.consumption || 0}
          err={additional.err}
          reset={props.reset}
        />
        <Efficiency
          isError={checkErrors("efficiency")}
          commonEff={additional.efficiency || 0}
          effectiveBage={effectiveBage}
          cons={consumption}
          perf={perf}
          algo={selectedAlgo}
          reset={props.reset}
        />
        <HashSelector
          hashOptions={Object.values(ALGOS_E).filter(
            (al) => al !== ALGOS_E.ALL
          )}
          name={formatMessage({ id: "filters.algo" })}
          id="hash"
          clear={false}
          initial={props.currentProfile?.hash}
          disabled={props.disabled}
          outlined={!props.disabled}
          onGetValue={onChangeAlgo}
          refresh={props.reset}
        />
      </div>
    </div>
  );
}

export default MinerProfileFields;
